import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { ProviderSettingsSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../../components/ToastNotifications";
import "./AIProviderSettings.css";
import { Can, useAccess } from "../../components/AccessContext";

const AIProviderSettings = () => {
  const { can } = useAccess();
  const canManage = can("admin.ai_providers.manage");
  const [providers, setProviders] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [savingTextModel, setSavingTextModel] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState("");
  const [testingProvider, setTestingProvider] = useState("");
  const [pageError, setPageError] = useState("");

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const result = await apiRequest("/api/admin/ai-providers");
      setProviders(Array.isArray(result.providers) ? result.providers : []);
      setDefaults(result.defaults || {});
    } catch (error) {
      setPageError(error.message || "Unable to load AI provider settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const displayedProviders = useMemo(() => providers.filter((provider) => provider.id !== "kling"), [providers]);
  const configuredProviders = displayedProviders.filter((provider) => provider.configured);

  const updateCredential = (providerId, field, value) => {
    setCredentials((current) => ({
      ...current,
      [providerId]: { ...current[providerId], [field]: value }
    }));
  };

  const saveProvider = async (event, provider) => {
    event.preventDefault();
    setSavingProvider(provider.id);
    try {
      const result = await apiRequest(`/api/admin/ai-providers/providers/${provider.id}`, {
        method: "PUT",
        body: JSON.stringify({
          apiKey: credentials[provider.id]?.apiKey || "",
          apiSecret: credentials[provider.id]?.apiSecret || "",
          enabled: !provider.available ? false : Boolean(provider.enabled)
        })
      });
      setProviders((current) => current.map((item) => item.id === provider.id ? result.provider : item));
      setCredentials((current) => ({ ...current, [provider.id]: { apiKey: "", apiSecret: "" } }));
      showSuccess(result.message || `${provider.name} settings saved securely.`);
      // Enabling or disabling a provider can re-route the default models on the server.
      apiRequest("/api/admin/ai-providers").then((latest) => setDefaults(latest?.defaults || {})).catch(() => {});
    } catch (error) {
      showError(error.message || `Unable to save ${provider.name} settings.`);
    } finally {
      setSavingProvider("");
    }
  };

  const saveTextModel = async (textModel) => {
    setSavingTextModel(true);
    try {
      const result = await apiRequest("/api/admin/ai-providers/defaults", { method: "PUT", body: JSON.stringify({ textModel }) });
      setDefaults(result.defaults || { ...defaults, textModel });
      showSuccess("Post writing model updated.");
    } catch (error) {
      showError(error.message || "Unable to update the post writing model.");
    } finally {
      setSavingTextModel(false);
    }
  };

  const testProvider = async (provider) => {
    setTestingProvider(provider.id);
    try {
      const result = await apiRequest(`/api/admin/ai-providers/providers/${provider.id}/test`, { method: "POST" });
      showSuccess(result.message || `${provider.name} connection verified.`);
    } catch (error) {
      showError(error.message || `${provider.name} connection test failed.`);
    } finally {
      setTestingProvider("");
    }
  };

  if (loading) return <main className="workspace-dashboard admin-ai-settings"><ProviderSettingsSkeleton label="Loading AI provider settings" /></main>;

  return <main className="workspace-dashboard admin-ai-settings">
    <header className="workspace-dashboard-header">
      <div>
        <span className="workspace-eyebrow">PLATFORM CONFIGURATION</span>
        <h1>AI provider settings</h1>
        <p>Connect your AI services and choose the models that power content generation.</p>
      </div>
      <button className="admin-ai-secondary" type="button" onClick={loadSettings} disabled={loading}>
        <i className="ti ti-refresh" aria-hidden="true" /> Refresh
      </button>
    </header>

    {pageError && <div className="admin-ai-error" role="alert">
      <i className="ti ti-alert-circle" aria-hidden="true" />
      <div><strong>Settings couldn’t be loaded</strong><p>{pageError}</p></div>
      <button className="admin-ai-secondary" type="button" onClick={loadSettings}>Try again</button>
    </div>}
    <section className="admin-ai-provider-section" aria-label="AI provider connections">
      <div className="admin-ai-provider-grid">
        {displayedProviders.map((provider) => <article className={`admin-ai-provider${provider.available ? "" : " is-unavailable"}`} key={provider.id}>
          <header className="admin-ai-provider-header">
            <div className={`admin-ai-provider-icon is-${provider.id}`} aria-hidden="true">
              <i className={`ti ${provider.id === "google" ? "ti-brand-google" : provider.id === "openai" ? "ti-sparkles" : provider.id === "anthropic" ? "ti-message-chatbot" : provider.id === "bfl" ? "ti-aperture" : "ti-video"}`} />
            </div>
            <div className="admin-ai-provider-title">
              <h3>{provider.name}</h3>
              <p>{provider.source === "settings" ? "Managed in admin settings" : provider.source === "environment" ? "Using server environment key" : "No credentials added"}</p>
            </div>
            <span className={`admin-ai-status ${!provider.available ? "is-unavailable" : provider.enabled && provider.configured ? "is-enabled" : "is-disabled"}`}>
              <i className={`ti ${!provider.available ? "ti-lock" : provider.enabled && provider.configured ? "ti-circle-check-filled" : "ti-circle-minus"}`} aria-hidden="true" />
              {!provider.available ? "Unavailable" : provider.enabled && provider.configured ? "Active" : "Inactive"}
            </span>
          </header>
          <div className="admin-ai-model-list" aria-label={`${provider.name} supported models`}>
            {Object.entries(provider.models || {}).filter(([, models]) => models.length > 0).map(([type, models]) => <div className="admin-ai-model-group" key={type}>
              <span>{type}</span>
              <div>{models.map((model) => <span className="admin-ai-model-chip" key={model.id}>{model.name}</span>)}</div>
            </div>)}
          </div>
          {provider.models?.text?.length > 0 && <label className="admin-ai-credential-field admin-ai-text-model"><span>Post writing model</span>
            <select value={provider.models.text.some((model) => model.id === defaults.textModel) ? defaults.textModel : ""} disabled={!canManage || !provider.available || !provider.enabled || !provider.configured || savingTextModel} onChange={(event) => event.target.value && saveTextModel(event.target.value)}>
              <option value="">{provider.enabled && provider.configured ? "Not used for post writing" : "Save and enable to use for post writing"}</option>
              {provider.models.text.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}
            </select>
          </label>}
          {provider.unavailableReason && <div className="admin-ai-warning" role="note"><i className="ti ti-alert-triangle" aria-hidden="true" /><p>{provider.unavailableReason}</p></div>}
          <form onSubmit={(event) => saveProvider(event, provider)}>
            <label className="admin-ai-credential-field"><span>{provider.credentialLabel}</span>
              <input type="password" autoComplete="new-password" minLength={8} maxLength={4096} value={credentials[provider.id]?.apiKey || ""} onChange={(event) => updateCredential(provider.id, "apiKey", event.target.value)} placeholder={provider.configured ? "Saved securely · enter to replace" : `Enter ${provider.credentialLabel.toLowerCase()}`} disabled={!provider.available} />
            </label>
            {provider.secretLabel && <label className="admin-ai-credential-field"><span>{provider.secretLabel}</span>
              <input type="password" autoComplete="new-password" minLength={8} maxLength={4096} value={credentials[provider.id]?.apiSecret || ""} onChange={(event) => updateCredential(provider.id, "apiSecret", event.target.value)} placeholder={provider.configured ? "Saved securely · enter to replace" : `Enter ${provider.secretLabel.toLowerCase()}`} disabled={!provider.available} />
            </label>}
            <label className="admin-ai-toggle"><input type="checkbox" checked={Boolean(provider.enabled)} disabled={!provider.available} onChange={(event) => setProviders((current) => current.map((item) => item.id === provider.id ? { ...item, enabled: event.target.checked } : item))} /><span className="admin-ai-switch" aria-hidden="true" /><span>Enable provider</span></label>
            <div className="admin-ai-provider-actions">
              <Can permission="admin.ai_providers.manage"><button className="admin-ai-primary" type="submit" disabled={!provider.available || savingProvider === provider.id}><i className={`ti ${savingProvider === provider.id ? "ti-loader-2" : "ti-device-floppy"}`} aria-hidden="true" />{savingProvider === provider.id ? "Saving…" : "Save changes"}</button></Can>
              <Can permission="admin.ai_providers.manage"><button className="admin-ai-secondary" type="button" onClick={() => testProvider(provider)} disabled={!provider.available || !provider.enabled || !provider.configured || testingProvider === provider.id}><i className={`ti ${testingProvider === provider.id ? "ti-loader-2" : "ti-plug"}`} aria-hidden="true" />{testingProvider === provider.id ? "Testing…" : "Test connection"}</button></Can>
            </div>
          </form>
        </article>)}
      </div>
    </section>
  </main>;
};

export default AIProviderSettings;
